"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Icon } from "./Icon";
import { useTranslations } from "next-intl";

interface SpecialtyCase {
  num: string;
  numShort: string;
  title: string;
  tag: string;
  icon: string;
  color: string;
  accent: string;
  intro: string;
  metricVal: string;
  metricLabel: string;
  generalProblem: string;
  chikitsaSolution: string;
  clinicalImpact: string;
}



export function EmrValuePropVisualizer({
  badPoints = [],
  goodPoints = []
}: {
  badPoints?: string[];
  goodPoints?: string[];
}) {
  const t = useTranslations("specializedEmr.valueProp");
  const SPECIALTY_CASES = t.raw("cases") as SpecialtyCase[];
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.3 });
  const stepperRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const elapsedRef = useRef(0);
  const lastCurrentRef = useRef(current);
  const DURATION = 6000;

  useEffect(() => {
    if (current !== lastCurrentRef.current) {
      elapsedRef.current = 0;
      lastCurrentRef.current = current;
      if (progressBarRef.current) {
        progressBarRef.current.style.width = "0%";
      }
    }
  }, [current]);

  useEffect(() => {
    if (stepperRef.current) {
      const activeEl = stepperRef.current.querySelector(".aiwf-step.active") as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [current]);

  useEffect(() => {
    if (!autoPlay || !isInView || isHovered) return;

    let lastTime = performance.now();
    let frameId: number;

    const tick = (now: number) => {
      const delta = now - lastTime;
      lastTime = now;
      elapsedRef.current += delta;

      if (elapsedRef.current >= DURATION) {
        elapsedRef.current = 0;
        setCurrent((c) => (c + 1) % SPECIALTY_CASES.length);
        lastCurrentRef.current = (current + 1) % SPECIALTY_CASES.length;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = "0%";
        }
        return;
      }

      if (progressBarRef.current) {
        const pct = Math.min((elapsedRef.current / DURATION) * 100, 100);
        progressBarRef.current.style.width = `${pct}%`;
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [autoPlay, isInView, isHovered, current]);

  const uc = SPECIALTY_CASES[current];

  return (
    <div className="w-full mt-8">
      <style>{`
        .emrvp-showcase {
          position: relative;
          width: 100%;
        }
        .emrvp-layout {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 36px;
          align-items: stretch;
          width: 100%;
          margin: 0 auto;
        }

        /* ─── LEFT STEPPER ─── */
        .emrvp-stepper {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding-right: 20px;
          border-right: 1px solid rgba(11,23,51,0.06);
          box-sizing: border-box;
        }
        .emrvp-stepper-label {
          font-family: var(--font-mono, monospace);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(11,23,51,0.35);
          margin-bottom: 12px;
          padding-left: 14px;
          flex-shrink: 0;
        }
        .aiwf-step {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          cursor: pointer;
          border: none;
          background: transparent;
          text-align: left;
          width: 100%;
          position: relative;
          border-radius: 12px;
          transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          border-left: 3px solid transparent;
          flex: 1;
          min-height: 44px;
        }
        .aiwf-step:hover:not(.active) {
          background: rgba(11,23,51,0.015);
        }
        .aiwf-step.active {
          background: var(--step-accent);
          border-left-color: var(--step-color);
        }
        .aiwf-step-num {
          font-family: var(--font-mono, monospace);
          font-size: 10px;
          font-weight: 700;
          color: rgba(11,23,51,0.4);
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(11,23,51,0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.22s ease;
        }
        .aiwf-step.active .aiwf-step-num {
          background: var(--step-color);
          color: #fff;
        }
        .aiwf-step-text {
          font-size: 13.5px;
          font-weight: 500;
          color: rgba(11,23,51,0.55);
          line-height: 1.35;
          transition: color 0.22s ease;
        }
        .aiwf-step.active .aiwf-step-text {
          color: var(--ink, #053C61);
          font-weight: 700;
        }

        /* ─── RIGHT CARD ─── */
        .emrvp-card-wrap {
          min-width: 0;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .emrvp-card {
          position: relative;
          border-radius: 24px;
          border: 1px solid rgba(11,23,51,0.07);
          background: #fff;
          box-shadow: 
            0 2px 1px rgba(11,23,51,0.01), 
            0 8px 24px rgba(11,23,51,0.03), 
            0 32px 64px rgba(11,23,51,0.04);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 480px;
        }
        .emrvp-progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 4px;
          background: var(--progress-color);
          width: 0%;
          z-index: 10;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
        }
        .emrvp-hero {
          padding: 32px 36px 24px;
          position: relative;
          overflow: hidden;
          background: #ffffff;
          border-bottom: 1px solid rgba(11,23,51,0.06);
        }
        .emrvp-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(11,23,51,0.04) 1px, transparent 1px);
          background-size: 16px 16px;
          pointer-events: none;
        }
        .emrvp-hero-splash {
          position: absolute;
          top: -90px;
          right: -90px;
          width: 320px;
          height: 320px;
          pointer-events: none;
          z-index: 0;
          transition: color 0.4s ease;
        }
        .emrvp-hero-splash svg {
          width: 100%;
          height: 100%;
        }
        .emrvp-hero-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
          position: relative;
          z-index: 1;
        }
        .emrvp-hero-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .emrvp-icon-pill {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-weight: 600;
          box-shadow: 0 4px 10px rgba(11,23,51,0.02);
        }
        .emrvp-num-label {
          font-family: var(--font-mono, monospace);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: rgba(11,23,51,0.4);
          text-transform: uppercase;
        }
        .emrvp-module-tag {
          font-size: 11px;
          font-weight: 700;
          padding: 6px 14px;
          border-radius: 99px;
          letter-spacing: 0.04em;
        }
        .emrvp-card-h2 {
          font-size: clamp(20px, 2.5vw, 25px);
          font-weight: 700;
          color: var(--ink, #053C61);
          letter-spacing: -0.02em;
          line-height: 1.25;
          margin: 0 0 10px;
          position: relative;
          z-index: 1;
        }
        .emrvp-card-p {
          font-size: 14.5px;
          color: rgba(11,23,51,0.65);
          line-height: 1.55;
          margin: 0;
          max-width: 640px;
          position: relative;
          z-index: 1;
        }

        /* ─── Metrics Section ─── */
        .emrvp-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          border-bottom: 1px solid rgba(11,23,51,0.06);
          background: rgba(11,23,51,0.005);
        }
        .emrvp-metric {
          padding: 16px 24px;
          border-right: 1px solid rgba(11,23,51,0.06);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }
        .emrvp-metric:last-child {
          border-right: none;
        }
        .emrvp-mval {
          font-size: 26px;
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1.1;
        }
        .emrvp-mlbl {
          font-size: 11px;
          color: rgba(11,23,51,0.45);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* ─── Triptych Section (Problem -> Solution -> Impact) ─── */
        .emrvp-triptych {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          flex: 1;
        }
        .emrvp-triptych-cell {
          padding: 24px 28px;
          border-right: 1px solid rgba(11,23,51,0.06);
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .emrvp-triptych-cell:last-child {
          border-right: none;
        }
        .emrvp-cell-head {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .emrvp-cell-label {
          font-family: var(--font-mono, monospace);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }
        .emrvp-cell-body {
          font-size: 13.5px;
          line-height: 1.5;
          color: rgba(11,23,51,0.7);
          margin: 0;
          font-weight: 500;
        }

        /* ─── Footer Section ─── */
        .emrvp-foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 28px;
          background: rgba(11,23,51,0.01);
          border-top: 1px solid rgba(11,23,51,0.05);
          flex-shrink: 0;
        }
        .emrvp-foot-v {
          font-size: 12px;
          font-weight: 600;
          color: rgba(11,23,51,0.5);
        }
        .emrvp-pips {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .emrvp-pip {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(11,23,51,0.12);
          border: none;
          cursor: pointer;
          padding: 0;
          transition: all 0.25s ease;
        }
        .emrvp-pip.active {
          width: 18px;
          border-radius: 3px;
        }

        @media (max-width: 960px) {
          .emrvp-layout {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .emrvp-stepper {
            flex-direction: row;
            overflow-x: auto;
            width: 100%;
            padding: 4px 6px;
            border-right: none;
            border-bottom: 1px solid rgba(11,23,51,0.08);
            gap: 12px;
            scrollbar-width: none;
          }
          .emrvp-stepper-label {
            display: none;
          }
          .aiwf-step {
            flex: 0 0 auto;
            width: auto;
            white-space: nowrap;
            padding: 8px 14px;
            border-left: none;
            border-bottom: 3px solid transparent;
            border-radius: 0;
          }
          .aiwf-step.active {
            background: transparent;
            border-bottom-color: var(--step-color);
          }
          .emrvp-triptych, .emrvp-metrics {
            grid-template-columns: 1fr;
          }
          .emrvp-triptych-cell, .emrvp-metric {
            border-right: none;
            border-bottom: 1px solid rgba(11,23,51,0.06);
            padding: 16px 20px;
          }
        }
      `}</style>

      <div className="emrvp-showcase">
        <div className="emrvp-layout" ref={containerRef}>
          {/* Left Vertical Stepper Navigation */}
          <nav ref={stepperRef} className="emrvp-stepper" aria-label="Specialty navigation">
            <span className="emrvp-stepper-label">{t("workflowTitle")}</span>
            {SPECIALTY_CASES.map((c, i) => (
              <button
                key={c.numShort}
                className={`aiwf-step${i === current ? " active" : ""}`}
                style={{
                  ["--step-color" as any]: c.color,
                  ["--step-accent" as any]: c.accent
                }}
                onClick={() => { setCurrent(i); setAutoPlay(false); }}
              >
                <span className="aiwf-step-num">{c.numShort}</span>
                <span className="aiwf-step-text">{c.title.split(" Workspace")[0]}</span>
              </button>
            ))}
          </nav>

          {/* Right Showcase Card */}
          <div
            className="emrvp-card-wrap"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 10, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.99 }}
                transition={{ duration: 0.4 }}
                className="emrvp-card"
              >
                {autoPlay && (
                  <div
                    ref={progressBarRef}
                    className="emrvp-progress-bar"
                    style={{ ["--progress-color" as any]: uc.color }}
                  />
                )}

                {/* Hero Header */}
                <div className="emrvp-hero">
                  {/* Solid abstract shape corner splash effect */}
                  <div className="emrvp-hero-splash" style={{ color: uc.color }}>
                    <svg viewBox="0 0 100 100" fill="currentColor" aria-hidden="true">
                      <path d="M30 0 C70 0, 100 30, 100 70 C100 85, 85 100, 70 100 C30 100, 0 70, 0 30 C0 15, 15 0, 30 0 Z" opacity="0.08" />
                      <path d="M40 10 C75 10, 95 30, 95 60 C95 72, 82 85, 70 85 C40 85, 15 65, 15 35 C15 22, 25 10, 40 10 Z" opacity="0.05" />
                    </svg>
                  </div>

                  <div className="emrvp-hero-top">
                    <div className="emrvp-hero-left">
                      <div
                        className="emrvp-icon-pill"
                        style={{ background: uc.accent, color: uc.color }}
                      >
                        <Icon name={uc.icon as any} width={20} height={20} />
                      </div>
                      <span className="emrvp-num-label">{uc.num}</span>
                    </div>
                    <span
                      className="emrvp-module-tag"
                      style={{ background: uc.accent, color: uc.color }}
                    >
                      {uc.tag}
                    </span>
                  </div>

                  <h2 className="emrvp-card-h2">{uc.title}</h2>
                  <p className="emrvp-card-p">{uc.intro}</p>
                </div>

                {/* Outcome Metrics Bar */}
                <div className="emrvp-metrics">
                  <div className="emrvp-metric">
                    <span className="emrvp-mval" style={{ color: uc.color }}>
                      {uc.metricVal}
                    </span>
                    <span className="emrvp-mlbl">{uc.metricLabel}</span>
                  </div>
                  <div className="emrvp-metric">
                    <span className="emrvp-mval" style={{ color: "#0A9E9A" }}>
                      {t("unified")}
                    </span>
                    <span className="emrvp-mlbl">{t("longitudinalRecord")}</span>
                  </div>
                  <div className="emrvp-metric">
                    <span className="emrvp-mval" style={{ color: "#1E7BD7" }}>
                      {t("connected")}
                    </span>
                    <span className="emrvp-mlbl">{t("himsAbdmEcosystem")}</span>
                  </div>
                </div>

                {/* Triptych Cells (General EMR vs Chikitsa vs Impact) */}
                <div className="emrvp-triptych">
                  {/* Cell 1: Traditional EMR Problem */}
                  <div className="emrvp-triptych-cell" style={{ background: "rgba(100,116,139,0.03)" }}>
                    <div className="emrvp-cell-head">
                      <span className="w-2 h-2 rounded-full bg-slate-400" />
                      <span className="emrvp-cell-label" style={{ color: "#64748B" }}>
                        {t("traditionalEmr")}
                      </span>
                    </div>
                    <p className="emrvp-cell-body">{uc.generalProblem}</p>
                  </div>

                  {/* Cell 2: Chikitsa Solution */}
                  <div className="emrvp-triptych-cell" style={{ background: "rgba(10,158,154,0.02)" }}>
                    <div className="emrvp-cell-head">
                      <Icon name="check" width={14} height={14} style={{ color: "#0A9E9A" }} />
                      <span className="emrvp-cell-label" style={{ color: "#0A9E9A" }}>
                        {t("chikitsaEmr")}
                      </span>
                    </div>
                    <p className="emrvp-cell-body">{uc.chikitsaSolution}</p>
                  </div>

                  {/* Cell 3: Clinical & Hospital Impact */}
                  <div className="emrvp-triptych-cell" style={{ background: "rgba(99,102,241,0.02)" }}>
                    <div className="emrvp-cell-head">
                      <Icon name="sparkle" width={14} height={14} style={{ color: "#6366F1" }} />
                      <span className="emrvp-cell-label" style={{ color: "#6366F1" }}>
                        {t("outcome")}
                      </span>
                    </div>
                    <p className="emrvp-cell-body">{uc.clinicalImpact}</p>
                  </div>
                </div>

                {/* Footer Navigation */}
                <div className="emrvp-foot">
                  <span className="emrvp-foot-v">
                    {t("ecosystem")}
                  </span>
                  <div className="emrvp-pips">
                    {SPECIALTY_CASES.map((c, i) => (
                      <button
                        key={c.numShort}
                        onClick={() => { setCurrent(i); setAutoPlay(false); }}
                        className={`emrvp-pip${i === current ? " active" : ""}`}
                        style={{
                          background: i === current ? uc.color : "rgba(11,23,51,0.12)"
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
